import PropTypes from "prop-types";
import React, { Component } from "react";
import { connect } from "react-redux";
import { injectIntl, intlShape, FormattedMessage } from "react-intl";
import CatalogueItem from "./CatalogueItem";
import TagSelector from "./TagSelector";
import Pager from "../pager";
import { loadLibraryCatalogueItems } from "actions/libraryCatalogueActions";
import { Loader } from "cxa-components";
import { getAltPageSize, SearchFilter } from "components/common/SearchFilter";
import { pageSizePresets } from "reducers/libraryCatalogueReducer";
import querystring from "querystring";
import messages from "components/messagesDescription";
import { Button } from "cxa-components";

const sortOptions = intl => [
  { name: intl.formatMessage(messages.sortSuggestedDescription), value: "" },
  {
    name: intl.formatMessage(messages.sortMostRecentDescription),
    value: "lastupdated",
  },
];

const SortByFilter = ({ options, value, onChange }) => (
  <p>
    <label htmlFor="sortByDropDown">
      <FormattedMessage {...messages.sortByDescription} />
    </label>{" "}
    <span className="cxa-c-form__select-box">
      <select name="sortBy" id="sortByDropDown" onChange={onChange} onBlur={onChange} value={value}>
        {options &&
          options.map(({ name, value }) => (
            <option key={value} value={value}>
              <FormattedMessage id={name} defaultMessage={name} />
            </option>
          ))}
      </select>
    </span>
  </p>
);

SortByFilter.propTypes = {
  options: PropTypes.array,
  value: PropTypes.string,
  onChange: PropTypes.func,
};

const removeDefaultParams = params =>
  Object.entries(params).reduce(
    (obj, [key, value]) =>
      value // eslint-disable-line no-nested-ternary
        ? Array.isArray(value) // eslint-disable-line no-nested-ternary
          ? value.length === 0 ? obj : { ...obj, [key]: value.sort().join(",") }
          : (key === "searchKeyword" && value === "") ||
            (key === "page" && value == 1) ||
            (key === "sortBy" && value == 0) ||
            (key === "pageSize" && value == pageSizePresets.lower)
            ? obj
            : { ...obj, [key]: value }
        : obj,
    {}
  );

const FilterBox = ({ items, linkifyTags, query }) => (
  <div>
    <header>
      <h4>
        <FormattedMessage {...messages.filterByTopicDescription} />
      </h4>
    </header>
    {items.tags &&
      items.tags.groups &&
      items.tags.groups.length > 0 && (
        <section>
          <TagSelector
            groups={items.tags.groups}
            linkify={linkifyTags}
            activeDomain={query.healthDomain}
            activeSubDomain={query.subDomain}
          />
        </section>
      )}
  </div>
);

FilterBox.propTypes = {
  items: PropTypes.object,
  linkifyTags: PropTypes.func,
  query: PropTypes.object,
};

const reloadData = ({ loadLibraryCatalogueItems, query }) =>
  loadLibraryCatalogueItems(removeDefaultParams(query));

class Catalogue extends Component {
  componentDidMount() {
    reloadData({ ...this.props });
  }

  reloadData(queryParams) {
    this.props.loadLibraryCatalogueItems(removeDefaultParams(queryParams));
  }

  // eslint-disable-next-line react/no-deprecated
  componentWillReceiveProps(nextProps) {
    const { query } = nextProps;
    if (query.searchKeyword !== this.props.query.searchKeyword) {
      this.setState({
        search: query.searchKeyword,
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.search !== this.props.location.search) {
      reloadData({ ...this.props });
    }
  }

  stringifyQuery = params => {
    return querystring.stringify(removeDefaultParams(params));
  };

  onSearchSubmit = value => {
    // Remove searchKeyword and page from the querystring
    const { searchKeyword, page, ...rest } = this.props.query;
    const additionalProps = value.length > 0 ? { searchKeyword: value } : {};
    this.updateQueryString(this.stringifyQuery({ ...rest, ...additionalProps }));
  };

  updateQueryString = queryStr => {
    const { history } = this.props;
    history.push(location.pathname + (queryStr.length > 0 ? "?" : "") + queryStr);
  };

  onSortByChange = e => {
    const { sortBy, sortDirection, page, ...rest } = this.props.query;
    const [finalSortBy, finalSortDirection] = e.currentTarget.value.split("_");
    const additionalProps = {
      ...(finalSortBy !== "" ? { sortBy: finalSortBy } : {}),
      ...(finalSortDirection && finalSortDirection !== ""
        ? { sortDirection: finalSortDirection }
        : {}),
    };
    this.updateQueryString(this.stringifyQuery({ ...rest, ...additionalProps }));
  };

  onTogglePageSize = () => {
    const { query, items } = this.props;
    const { pageSize } = items;
    this.updateQueryString(this.stringifyQuery({ ...query, pageSize: getAltPageSize(pageSize) }));
  };

  render() {
    const { fetching, items, query, location, intl } = this.props;

    if (!items) {
      return <Loader />;
    }

    const currentPage = parseInt(query.page || 1);
    const total = items.recordsFound || 0;
    const { tags: { searchKeyword, searchTag: { name: searchDomain } = {} } = {} } = items;
    let catalogueElements;

    if (items.contents && items.contents.length > 0) {
      catalogueElements = items.contents.map((item, index) => (
        <CatalogueItem item={item} key={index} />
      ));
    }

    const { sortBy } = query;
    const { pageSize } = items;

    const link = target =>
      `${location.pathname}?${this.stringifyQuery({ ...query, page: target })}`;
    const linkifyTags = target =>
      `${location.pathname}?${this.stringifyQuery({
        ...query,
        ...target,
        page: null,
      })}`;

    const filterDescription = [searchKeyword, searchDomain].filter(x => x);
    const longFilterDescription = filterDescription.join(", ");
    const htmlFilters = intl.formatMessage(messages.showingAllTextDescription, {
      moreDescription: filterDescription.length,
      total,
      filters: longFilterDescription,
    });

    const urlQuery = this.stringifyQuery({ pageSize, sortBy });
    const clearFilterLinkUrl = urlQuery.length ? `?${urlQuery}` : ".";
    const clearFilterLink = filterDescription.length > 0 && (
      <span>
        {" "}
        <Button link href={clearFilterLinkUrl}>
          <FormattedMessage {...messages.filterBox_resultClearFilter} />
        </Button>
      </span>
    );

    return (
      <div>
        <SearchFilter
          filterBox={FilterBox}
          filterParams={{
            items: items,
            linkifyTags: linkifyTags,
            query: query,
          }}
          onSearchSubmit={this.onSearchSubmit}
          onTogglePageSize={this.onTogglePageSize}
          pageSize={pageSize}
          sortOptions={sortOptions(intl)}
          sortValue={sortBy}
          onSortChange={this.onSortByChange}
          intl={intl}
        />
        {!fetching && (
          <p>
            <span>{htmlFilters}</span>
            {clearFilterLink}
          </p>
        )}
        <div className="apl-library__wrapper">
          {fetching ? (
            <Loader />
          ) : (
            <div>
              <div className="cxa-grid__wrapper apl-showcase">{catalogueElements}</div>
              <Pager total={total} currentPage={currentPage} pageSize={pageSize} link={link} />
            </div>
          )}
        </div>
      </div>
    );
  }
}

Catalogue.propTypes = {
  query: PropTypes.object,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  error: PropTypes.object,
  search: PropTypes.string,
  items: PropTypes.object,
  fetching: PropTypes.bool.isRequired,
  loadLibraryCatalogueItems: PropTypes.func,
  push: PropTypes.func.isRequired,
  intl: intlShape.isRequired,
};

export default injectIntl(
  connect(
    state => ({
      error: state.libraryCatalogueItems.error,
      items: state.libraryCatalogueItems.data,
      fetched: state.libraryCatalogueItems.fetched,
      fetching: state.libraryCatalogueItems.fetching,
    }),
    {
      loadLibraryCatalogueItems,
    }
  )(Catalogue)
);



// WEBPACK FOOTER //
// ./components/elibrary/CataloguePage.js